<template>
  <icon-base
    v-if="!isFavorite"
    v-tooltip="{
      content: $t('WORKFLOWS.WORKFLOW_BUILDER.PRIMARY_SCORECARD_MESSAGE'),
      placement: 'left',
    }"
    :icon="IconStarOutlined"
    :class="pointerClass"
    data-cy="primary-scorecard-button"
    @click="handleFavoriteClick"
  />
  <icon-base
    v-else
    :icon="IconStar"
    :class="pointerClass"
    @click="handleFavoriteClick"
  />
</template>

<script setup lang="ts">
import { computed, inject } from "vue";
import { useStore } from "vuex";

import { Ability, PermissionSubject } from "@/enums/auth";
import { ScorecardType } from "@/enums/scorecards";
import { modelIsScorecardGroup } from "@/helpers/scorecards";
import { ScorecardsIsWorkflowBuilder } from "@/helpers/symbols";
import { usePermissions } from "@/hooks/auth";
import { useDealsBase } from "@/hooks/deals";
import { useActiveStage, useActiveWorkflowTemplate } from "@/hooks/workflow";

import IconStar from "@/components/icons/IconStar.vue";
import IconStarOutlined from "@/components/icons/IconStarOutlined.vue";
import IconBase from "@/components/ui/IconBase.vue";

import type { IScorecard, IScorecardGroup } from "@/models/scorecards";
import type { PropType } from "vue";

const props = defineProps({
  model: {
    type: Object as PropType<IScorecard | IScorecardGroup>,
    required: true,
  },
});

const { activeDeal } = useDealsBase();
const { dispatch, commit } = useStore();
const { activeStage } = useActiveStage();
const { canPerformActionReactive } = usePermissions();
const { canEditWorkflowTemplate } = useActiveWorkflowTemplate();

const isWorkflowBuilder = inject(ScorecardsIsWorkflowBuilder);
const isScoreCardsGroup = computed(() => modelIsScorecardGroup(props.model));

const canSetPrimary = canPerformActionReactive(
  PermissionSubject[isScoreCardsGroup.value ? "scoreCardGroup" : "scoreCard"],
  Ability[
    isScoreCardsGroup.value
      ? "updatePrimaryScorecardGroup"
      : "updatePrimaryScorecard"
  ],
  {
    [isScoreCardsGroup.value ? "scoreCardGroup" : "scoreCard"]: props.model.id,
    application: activeDeal.value.id,
    scoreCardStageId: activeStage.value?.id,
  }
);

const isFavorite = computed(() => {
  if (isWorkflowBuilder) {
    return isScoreCardsGroup.value
      ? props.model.id === activeStage.value?.primary_group
      : props.model.id === activeStage.value?.primary_card;
  } else {
    return isScoreCardsGroup.value
      ? props.model.id ===
          (activeDeal.value.score_card_group_id ||
            (!activeDeal.value.score_card_id &&
              activeStage.value?.primary_group))
      : props.model.id ===
          (activeDeal.value.score_card_id ||
            (!activeDeal.value.score_card_group_id &&
              activeStage.value?.primary_card));
  }
});

const pointerClass = computed(() =>
  canSetPrimary.value || isWorkflowBuilder
    ? "cursor-pointer"
    : "cursor-not-allowed"
);

const setPrimary = (): void => {
  isScoreCardsGroup.value
    ? commit("workflows/setPrimaryScorecardGroup", props.model.id)
    : commit("workflows/setPrimaryScorecard", props.model.id);
};

const handleFavoriteClick = async () => {
  if (isWorkflowBuilder) {
    if (!canEditWorkflowTemplate.value) {
      return;
    }
    setPrimary();
  } else if (!isFavorite.value && canSetPrimary.value) {
    await dispatch("applications/setPrimaryScorecard", {
      scorecardId: props.model.id,
      type: isScoreCardsGroup.value ? ScorecardType.GROUP : ScorecardType.CARD,
    });
  }
};
</script>
